/**
 * The one email-shape regex for the whole platform. Requires a dotted domain
 * with a 2+ char TLD — the stricter of the two variants that used to be
 * copy-pasted around (the old `\.[^\s@]+$` accepted single-char TLDs like
 * `a@b.c`). Single source for every sign-surface client gate AND server-side
 * validation, so the server can't be looser than the UI on a legal-record field.
 *
 * `packages/shared-utils/src/waiver.ts` re-exports this; the Vercel serverless
 * bundle carries an inlined copy at `apps/portal/api/_lib/emailValidation.ts`
 * (guarded by `apps/portal/tests/unit/emailValidationInline.test.ts`).
 */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/** RFC 5321 practical maximum length for a full email address. */
export const EMAIL_MAX_LENGTH = 254

/**
 * Common domain-typo corrections (gmial.com → gmail.com). Shared by both apps'
 * `/api/validate-email` endpoints (portal Vercel fn + storefront route). The
 * portal function reads the inlined `_lib/emailValidation.ts` copy; the
 * storefront route imports this one directly.
 */
export const DOMAIN_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmali.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmaul.com': 'gmail.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yhaoo.com': 'yahoo.com',
  'yaoo.com': 'yahoo.com',
  'hotmal.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmali.com': 'hotmail.com',
  'hotamil.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outlool.com': 'outlook.com',
  'iclod.com': 'icloud.com',
  'icloud.co': 'icloud.com',
  'icould.com': 'icloud.com',
  'aol.co': 'aol.com',
}

/** Very basic email format check. */
export function isValidEmail(email: string): boolean {
  return EMAIL_RE.test(email)
}

export type EmailMxResult = { valid: boolean; suggestion?: string } | null

/**
 * Validate an email's domain by calling the host app's `/api/validate-email`
 * endpoint (portal = Vercel function, storefront = Next.js route handler).
 * Returns `{valid, suggestion}` or `null` (not a well-formed email, or
 * network failure — caller should render nothing).
 *
 * Used as a post-save / on-blur annotation — never a hard block, since MX
 * lookups can flake or legitimate domains can be slow to propagate.
 */
export async function validateEmailMx(email: string): Promise<EmailMxResult> {
  const trimmed = email.trim()
  if (!trimmed || !isValidEmail(trimmed)) return null
  try {
    const res = await fetch('/api/validate-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: trimmed }),
    })
    if (!res.ok) return null
    return (await res.json()) as EmailMxResult
  } catch {
    return null
  }
}
